import type { RegistryState } from '../../data/types';
import type { RegistryAction } from '../actions';
import { fundCode, today, withAudit } from '../helpers';

type Handler<T extends RegistryAction['type']> = (
  state: RegistryState,
  action: Extract<RegistryAction, { type: T }>,
) => RegistryState;

export const importUnitPrices: Handler<'UNIT_PRICES_IMPORT'> = (state, action) => {
  const unitClass = state.classes.find((item) => item.id === action.classId);
  if (!unitClass || action.rows.length === 0) return state;
  const dates = new Set(action.rows.map((row) => row.date));
  const history = [
    ...unitClass.priceHistory.filter((point) => !dates.has(point.date)),
    ...action.rows.map((row) => ({
      date: row.date,
      price: row.mid,
      entry: row.entry,
      exit: row.exit,
    })),
  ].sort((a, b) => a.date.localeCompare(b.date));
  const current = history.filter((point) => point.date <= today).pop();
  const next: RegistryState = {
    ...state,
    classes: state.classes.map((item) =>
      item.id === action.classId
        ? { ...item, priceHistory: history, unitPrice: current?.price ?? item.unitPrice }
        : item,
    ),
  };
  return withAudit(next, 'Unit prices imported', unitClass.name, `${action.rows.length} dates`);
};

export const setUnitPrice: Handler<'UNIT_PRICE_SET'> = (state, action) => {
  const unitClass = state.classes.find((item) => item.id === action.classId);
  if (!unitClass) return state;
  const next: RegistryState = {
    ...state,
    classes: state.classes.map((item) =>
      item.id === action.classId
        ? {
            ...item,
            unitPrice: action.price,
            priceHistory: [
              ...item.priceHistory.filter((point) => point.date !== action.date),
              { date: action.date, price: action.price },
            ].sort((a, b) => a.date.localeCompare(b.date)),
          }
        : item,
    ),
  };
  return withAudit(
    next,
    'Unit price published',
    `${fundCode(state, unitClass.fundId)} ${unitClass.name}`,
    `$${action.price.toFixed(4)} effective ${action.date}`,
  );
};
